import { LoggerFactory } from "lego-gui-lib";
import {
  Required,
  Schema,
  TextField,
  RemoteDataSource,
  ForeignKeyField
} from "lego-gui-lib/lib";
import { createContext } from "react";
import { mainStore, api } from "../../core/mainStore";
import { t } from "../../core/i18n/i18n";
import { Auth, HnsUser } from "../../core/model/auth";
import { withExternalId } from "../../common/common";
import { showErrorMessage } from "../tree/treeBlock/schema/validators";
import { runInAction } from "mobx";
import { ForeignKeyFieldWithPageSize } from "../../common/ForeignKeyFieldWithPageSize";

const log = LoggerFactory.create("CustomerSchema");

export const CustomerSchema = new Schema({
  fields: [
    {
      name: "name",
      title: t("common:name"),
      type: TextField(Required),
      showTooltip: true
    },
    {
      name: "externalId",
      title: t("hns:customer:externalId"),
      type: TextField(Required)
    }
  ],
  primaryKey: ["id"],
  defaultSort: { sort: "externalId", order: "asc" },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ADMIN
  }
});

mainStore.customer = RemoteDataSource.create({
  api: api,
  name: "customer",
  schema: CustomerSchema,
  url: "/customers",
  commons: mainStore.commons
});

export const customerContext = createContext(mainStore.customer);

mainStore.customerFk = RemoteDataSource.clone(mainStore.customer);

/** This mechanism distinguishes on login between Admin, SAN Admin and Customer privileges for customer aware resources */
mainStore.commons.on("auth.profile", ([currentUser]: HnsUser[]) => {
  if (mainStore.auth.isCustomer()) {
    initCustomerRefForCustomer(currentUser);
  }
  if (mainStore.auth.isSanAdmin()) {
    initCustomerRefForAnyCustomerOwner();
  }
});

const initCustomerRefForCustomer = (currentUser: HnsUser): void => {
  const customerId =
    window.localStorage.getItem("customerOwnerLastCustomerRef") ||
    currentUser?.customerId;
  mainStore.customer.oneDataSource.findOne({ id: customerId });
  /** Assign selected customer role on init for Customer Owner */
  if (mainStore.auth.isCustomerOwner()) {
    runInAction(() => {
      mainStore.auth.assignCustomerRole(customerId);
    });
  }
};

const initCustomerRefForAnyCustomerOwner = async () => {
  try {
    await mainStore.customer.listDataSource.findAll();
    const firstAvailableCustomerRef =
      mainStore.customer.listDataSource?.list[0]?.id;

    if (firstAvailableCustomerRef) {
      mainStore.customer.oneDataSource.findOne({
        id: firstAvailableCustomerRef
      });
    }
  } catch (e) {
    showErrorMessage("hns:customer:initWithFirstFailed");
    log.error("Init first available customerRef failed", e);
  }
};

export const CustomerFK = ForeignKeyFieldWithPageSize(
  {
    dataSource: mainStore.customerFk.listDataSource,
    searchField: "name",
    labelField: withExternalId(),
    valueField: "id",
    pageSize: 30000
  },
  Required
);
